/**
 * CMS image overrides loader.
 *
 * The public homepage reads a small map of slot -> {url} from /api/cms/overrides
 * (admin-uploaded hero/category/brand imagery). We fetch it once per session and
 * cache it in module memory. Components fetch it as part of their data load and
 * fall back to auto-generated imagery when a slot has no override.
 *
 * Uses native fetch (not the shared axios instance) and retries a few times,
 * because the same-origin /api proxy can transiently 502/timeout while many
 * requests fire during the initial page load. A failed attempt is never cached
 * as an empty result, so a later call can still succeed.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const OVERRIDES_URL = `${BACKEND_URL}/api/cms/overrides`;

let _cache = null;
let _inflight = null;

const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function _fetchOnce() {
  const r = await fetch(OVERRIDES_URL, { cache: "no-store" });
  if (!r.ok) throw new Error(`overrides ${r.status}`);
  const data = await r.json();
  return data && typeof data === "object" ? data : {};
}

async function _fetchWithRetry(attempts = 3) {
  let lastErr;
  for (let i = 0; i < attempts; i += 1) {
    try {
      return await _fetchOnce();
    } catch (e) {
      lastErr = e;
      await _sleep(400 * (i + 1));
    }
  }
  throw lastErr;
}

export async function loadCmsOverrides(force = false) {
  if (_cache && !force) return _cache;
  if (_inflight && !force) return _inflight;
  _inflight = _fetchWithRetry()
    .then((data) => {
      _cache = data; // only cache a successful response
      return _cache;
    })
    .catch(() => _cache || {}) // never poison the cache on failure
    .finally(() => { _inflight = null; });
  return _inflight;
}

/** Invalidate + refetch (call after an admin upload/reset). */
export function refreshCmsOverrides() {
  return loadCmsOverrides(true);
}
